import {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {PATHS_API} from "../../../enum";

interface IStats {
    opens: number,
    spent: number,
    avgDropPrice: number,
    payoutRatio: number,
    refunded: number,
    refundRate: number,
    refundedValue: number,
    items: { name: string, price: number, count: number, share: number, refunded: number, refundRate: number }[],
}

interface IDrop {
    _id: string,
    date: string,
    steamId: string,
    server?: string,
    name: string,
    variant?: string,
    price: number,
    paid?: number,
    refunded: boolean,
    isIssued: boolean,
}

interface IServer { _id: string, name: string }

const PAGE = 50;
const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
const uah = (v: number) => `${Math.round(v)} ₴`;

// Статистика ящика удачі: що випадає, середня ціна дропу, як часто предмет повертають
const LootboxStats = ({ onError }: { onError: (message: string) => void }) => {
    const [open, setOpen] = useState(false);
    const [days, setDays] = useState(30);
    const [server, setServer] = useState("");
    const [servers, setServers] = useState<IServer[]>([]);
    const [stats, setStats] = useState<IStats | null>(null);
    const [drops, setDrops] = useState<IDrop[]>([]);
    const [more, setMore] = useState(true);

    const params = (extra: object = {}) => ({ days, ...(server ? { server } : {}), ...extra });

    const load = useCallback(async () => {
        try {
            const [s, d] = await Promise.all([
                axios.get(`${PATHS_API.ADMIN_LOOTBOX}/stats`, { params: params(), withCredentials: true }),
                axios.get(`${PATHS_API.ADMIN_LOOTBOX}/drops`, { params: params({ limit: PAGE }), withCredentials: true }),
            ]);
            setStats(s.data);
            setDrops(d.data);
            setMore(d.data.length === PAGE);
        } catch (err: any) {
            onError(err.response?.data?.message || "Не вдалося завантажити статистику ящика");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, server]);

    const loadMore = async () => {
        try {
            const { data } = await axios.get(`${PATHS_API.ADMIN_LOOTBOX}/drops`, { params: params({ limit: PAGE, skip: drops.length }), withCredentials: true });
            setDrops((list) => [...list, ...data]);
            setMore(data.length === PAGE);
        } catch (err: any) {
            onError(err.response?.data?.message || "Не вдалося завантажити ще");
        }
    };

    useEffect(() => {
        if (!open) return;
        axios.get(PATHS_API.ADMIN_SERVERS, { withCredentials: true }).then(({ data }) => setServers(data)).catch(() => {});
    }, [open]);

    useEffect(() => { if (open) load(); }, [open, load]);

    return (
        <div className="adm-lootbox adm-lootbox-stats">
            <div className="adm-lootbox-title">
                <b>Статистика ящика</b>
                <button type="button" className="adm-ghost-btn" onClick={() => setOpen(!open)}>{open ? "Сховати" : "Показати"}</button>
            </div>

            { open && <>
                <div className="adm-toolbar">
                    <select className="adm-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                        <option value={1}>За добу</option>
                        <option value={7}>За 7 днів</option>
                        <option value={30}>За 30 днів</option>
                        <option value={0}>За весь час</option>
                    </select>
                    <select className="adm-select" value={server} onChange={(e) => setServer(e.target.value)}>
                        <option value="">Усі сервери</option>
                        { servers.map((s) => <option key={s._id} value={s._id}>{s.name}</option>) }
                    </select>
                </div>

                { stats && <>
                    <div className="adm-stats">
                        <div><b>{stats.opens}</b><span>відкрито</span></div>
                        <div><b>{uah(stats.spent)}</b><span>витратили гравці</span></div>
                        <div><b>{uah(stats.avgDropPrice)}</b><span>середня ціна дропу</span></div>
                        <div title="Вартість дропу на 1 грн, витрачену гравцями. Більше 100% — магазин у мінусі"><b>{pct(stats.payoutRatio)}</b><span>віддача гравцям</span></div>
                        <div><b>{pct(stats.refundRate)}</b><span>повертають ({stats.refunded})</span></div>
                        <div><b>{uah(stats.refundedValue)}</b><span>повернуто грошима</span></div>
                    </div>

                    <table className="adm-table">
                        <thead><tr><th>Предмет</th><th>Ціна</th><th>Випало</th><th>Частка</th><th>Повернули</th></tr></thead>
                        <tbody>
                            { stats.items.map((i) =>
                                <tr key={i.name}>
                                    <td>{i.name}</td>
                                    <td>{uah(i.price)}</td>
                                    <td>{i.count}</td>
                                    <td>{pct(i.share)}</td>
                                    <td>{i.refunded} ({pct(i.refundRate)})</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </> }

                <table className="adm-table">
                    <thead><tr><th>Час</th><th>Гравець</th><th>Сервер</th><th>Випало</th><th>Ціна</th><th>Заплатив</th><th>Статус</th></tr></thead>
                    <tbody>
                        { drops.map((d) =>
                            <tr key={d._id}>
                                <td>{new Date(d.date).toLocaleString("uk-UA")}</td>
                                <td>{d.steamId}</td>
                                <td>{d.server}</td>
                                <td>{d.name}{d.variant ? ` (${d.variant})` : ""}</td>
                                <td>{d.price != null ? uah(d.price) : "—"}</td>
                                <td>{d.paid != null ? uah(d.paid) : "—"}</td>
                                <td>{d.refunded ? "повернув" : d.isIssued ? "видано" : "у кошику"}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                { more && <button type="button" className="adm-ghost-btn" onClick={loadMore}>Показати ще</button> }
            </> }
        </div>
    );
};

export default LootboxStats;
